/* eslint-disable @typescript-eslint/no-explicit-any */
import generateComponentStyles from './styleHelper';
import { withRootFlexDefaults, isColumnRootDirection, RootFlexStyle } from './rootFlexDefaults';
import { rowSizeGlobal, colCountGlobal } from '../config/config';

/**
 * How a screen's ROOT level is laid out — the one answer both the published page
 * (`routes/page`) and the editor's view-mode frame (`routes/internalFrame`) render.
 *
 * The frame used to keep its own copy: a hardcoded `display: grid` wrapper built from
 * `settings.cols/rows` that never read `settings.style.display`. A flex screen (every
 * screen the editor creates) therefore rendered as a 1×1 grid in view mode — the first
 * root section took the single `1fr` row and every following one fell into an implicit
 * row below the frame, so two side-by-side cubes on the canvas showed up as ONE in view
 * mode while the published page laid them out in a row.
 */

const FLEX_ROOT_ID = '__flex_root__';
const isFlexRootNode = (node: any): boolean => !!node && node.id === FLEX_ROOT_ID;
const pickSoleFlexRoot = (x: any): any => {
  if (Array.isArray(x) && x.length === 1 && isFlexRootNode(x[0])) return x[0];
  if (x && !Array.isArray(x) && isFlexRootNode(x)) return x;
  return null;
};

export type TopLayerParentStyle =
  | { display: 'flex'; flex: RootFlexStyle }
  | { display: 'grid' };

export interface RootLayout {
  /** The page tree, with any legacy `__flex_root__` wrapper chain unwrapped. */
  items: any;
  /** `settings.style` minus the layout-mode fields — the outer `.page` background. */
  pageBackgroundStyle: Record<string, any>;
  /** Inline style for `.pageWrap`, the actual flex/grid parent of the root sections. */
  wrapStyle: Record<string, any>;
  /** Column root → `.pageWrapColumn` (pins root children, see page.module.scss). */
  isColumnRoot: boolean;
  /** `parentStyle` for every top-level node — see the note where it is built. */
  topLayerParentStyle: TopLayerParentStyle;
}

export function resolveRootLayout(page: any, settings: any): RootLayout {
  // Back-compat: legacy screens persisted the editor's __flex_root__ wrapper as a
  // top-level section. Unwrap it here so the runtime tree matches the new clean format.
  // Loop unwraps arbitrarily-deep chains caused by multiple saves before the save-side strip.
  // Keep the outermost wrapper so its style (if flex) can drive the top-level layout.
  const legacyFlexRoot = pickSoleFlexRoot(page);
  let unwrapped: any = legacyFlexRoot?.items ?? page;
  let nested = pickSoleFlexRoot(unwrapped);
  while (nested) {
    unwrapped = nested.items ?? unwrapped;
    nested = pickSoleFlexRoot(unwrapped);
  }

  // Strip layout-mode fields (display, flex) from settings.style before applying to the
  // outer .page wrapper — those are canvas layout properties, not page background styles.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { display: _sd, flex: _sf, ...pageBackgroundStyle } = (settings?.style || {}) as any;

  // Top-level layout mode. Prefer settings.style; fall back to the legacy flex_root's style
  // so old backend payloads keep rendering correctly.
  const layoutStyle = (legacyFlexRoot?.style || settings?.style || {}) as any;
  const isFlexLayout = layoutStyle?.display === 'flex';
  // TNBEWEB-2019: resolve the root flex object ONCE, defaults filled in, and use
  // that one value for both the wrapper style and the children's `parentStyle`.
  // A screen whose Layout was never touched has no `flex` at all; leaving the
  // gap open let `.pageWrap`'s old `justify-content: center` decide the root
  // alignment here while the editor's canvas used the browser default, which is
  // the mismatch this ticket is about. Fields the screen DOES declare still win
  // — a legacy `__flex_root__` wrapper style keeps overriding every one of them.
  const rootFlex = isFlexLayout ? withRootFlexDefaults(layoutStyle?.flex) : null;
  const flexWrapStyle = isFlexLayout
    ? generateComponentStyles({ display: 'flex', flex: rootFlex })
    : null;

  const wrapStyle = isFlexLayout
    ? flexWrapStyle || { display: 'flex' }
    : {
      display: 'grid',
      gridTemplateColumns: `repeat(${settings?.cols || colCountGlobal}, 1fr)`,
      gridAutoRows: settings?.rows ? undefined : `${rowSizeGlobal}px`,
      gridTemplateRows: settings?.rows ? `repeat(${settings?.rows}, 1fr)` : undefined,
    };

  // Synthetic parentStyle for the top-level container. .pageWrap is the actual flex/grid
  // parent in the DOM, but Container only reads parent layout from a `parentStyle` prop —
  // without this, `width/height: 'fill'` on a root container resolves to undefined and the
  // container collapses to 0×0, hiding any fill.
  // `rootFlex`, not the raw `layoutStyle.flex` — this is what resolves a child's
  // `width/height: 'fill'` (main axis → flex-grow, cross axis → 100%), so it has
  // to see the same direction the wrapper is actually laid out with.
  const topLayerParentStyle: TopLayerParentStyle = isFlexLayout
    ? { display: 'flex', flex: rootFlex as RootFlexStyle }
    : { display: 'grid' };

  return {
    items: unwrapped,
    pageBackgroundStyle,
    wrapStyle,
    isColumnRoot: isFlexLayout && isColumnRootDirection(rootFlex),
    topLayerParentStyle,
  };
}
